import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { supabase } from "../lib/supabase";

const SIZE_MAP = {
  full:      { width: '100%',  maxWidth: '100%' },
  large:     { width: '75%',   maxWidth: '75%'  },
  medium:    { width: '50%',   maxWidth: '50%'  },
  small:     { width: '250px', maxWidth: '250px' },
  thumbnail: { width: '120px', maxWidth: '120px' },
};

function Lightbox({ src, onClose }) {
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.92)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      cursor: 'zoom-out',
    }}>
      <img src={src} alt="" style={{ maxWidth: '92vw', maxHeight: '92vh', borderRadius: 8, objectFit: 'contain' }} />
      <button onClick={onClose} style={{
        position: 'absolute', top: 20, right: 24,
        background: 'none', border: 'none', color: '#fff', fontSize: 28, cursor: 'pointer',
      }}>✕</button>
    </div>
  );
}

function CmsImage({ s, title, onClick }) {
  if (!s?.image_url) return null;
  const size    = SIZE_MAP[s.image_size] || SIZE_MAP.full;
  const align   = s.image_align || 'center';
  const clickable = s.image_clickable;
  const wrapStyle = {
    display: 'flex', flexDirection: 'column', marginBottom: 20,
    alignItems: align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center',
  };
  return (
    <div style={wrapStyle}>
      <img
        src={s.image_url} alt={title || ''}
        onClick={clickable ? () => onClick(s.image_url) : undefined}
        onError={(e) => { e.target.style.display = 'none'; }}
        style={{
          ...size, borderRadius: 12, objectFit: 'cover', display: 'block',
          cursor: clickable ? 'zoom-in' : 'default',
          transition: 'transform 0.2s, box-shadow 0.2s',
        }}
        onMouseEnter={clickable ? (e) => {
          e.currentTarget.style.transform = 'scale(1.02)';
          e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.4)';
        } : undefined}
        onMouseLeave={clickable ? (e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = 'none';
        } : undefined}
      />
      {s.image_caption && (
        <p style={{ fontSize: 12, color: '#777', marginTop: 6, textAlign: 'center' }}>{s.image_caption}</p>
      )}
    </div>
  );
}

const DEFAULT_GOALS = {
  en: [
    "Promoting plastic art in Sudan and highlighting its positive role in Sudanese society and culture.",
    "Expanding the network of communication and acquaintance between plastic artists in Sudan and artists worldwide.",
    "Displaying the works of plastic artists, exchanging experiences and ideas across borders.",
    "Providing opportunities and practical support for plastic artists in Sudan.",
    "Enhancing artistic awareness in Sudanese society and encouraging a deeper interest in art.",
    "Expressing Sudanese culture and promoting it to the world through plastic art.",
    "Preserving the legacy of Sudan's pioneer visual artists for future generations.",
  ],
  ar: [
    "تعزيز الفن التشكيلي في السودان وإبراز دوره الإيجابي في المجتمع والثقافة السودانية.",
    "توسيع شبكة التواصل والتعارف بين الفنانين التشكيليين في السودان والفنانين حول العالم.",
    "عرض أعمال الفنانين التشكيليين، وتبادل الخبرات والأفكار عبر الحدود.",
    "توفير الفرص والدعم العملي للفنانين التشكيليين في السودان.",
    "تعزيز الوعي الفني في المجتمع السوداني وتشجيع الاهتمام العميق بالفنون.",
    "التعبير عن الثقافة السودانية وتعريف العالم بها من خلال الفن التشكيلي.",
    "الحفاظ على إرث رواد الفنون البصرية السودانيين للأجيال القادمة.",
  ],
};

function About() {
  const { t, lang } = useApp();
  const isAr = lang === "ar";
  const [cms,      setCms]      = useState({});
  const [lightbox, setLightbox] = useState(null);
  const openLightbox = useCallback((src) => setLightbox(src), []);

  useEffect(() => {
    supabase
      .from("page_sections")
      .select("*")
      .eq("page", "about")
      .eq("is_visible", true)
      .order("sort_order", { ascending: true })
      .then(({ data }) => {
        if (data) {
          const map = {};
          data.forEach((s) => { map[s.section_key] = s; });
          setCms(map);
        }
      });
  }, []);

  // Get text from CMS or fall back to default
  const cBody = (key, defaultEn, defaultAr) => {
    const s = cms[key];
    if (!s) return isAr ? defaultAr : defaultEn;
    return isAr ? (s.body_ar || s.body_en || defaultAr) : (s.body_en || s.body_ar || defaultEn);
  };

  const cTitle = (key, defaultEn, defaultAr) => {
    const s = cms[key];
    if (!s) return isAr ? defaultAr : defaultEn;
    return isAr ? (s.title_ar || s.title_en || defaultAr) : (s.title_en || s.title_ar || defaultEn);
  };

  // Goals: from CMS body (one per line) or default array
  const goalList = (() => {
    const s = cms["mission"];
    if (s) {
      const body = isAr ? (s.body_ar || s.body_en) : (s.body_en || s.body_ar);
      if (body) return body.split("\n").filter(Boolean);
    }
    return DEFAULT_GOALS[lang] || DEFAULT_GOALS.en;
  })();

  // Extra free-form sections from CMS (any section_key not already handled above)
  const HANDLED = new Set(["identity", "mission", "heritage"]);
  const extraSections = Object.values(cms)
    .filter((s) => !HANDLED.has(s.section_key))
    .sort((a, b) => a.sort_order - b.sort_order);

  return (
    <>
      {lightbox && <Lightbox src={lightbox} onClose={() => setLightbox(null)} />}

      <div className="page-band">
        <div className="container">
          <span className="eyebrow">{t("aboutEyebrow")}</span>
          <h1>{t("aboutTitle")}</h1>
        </div>
      </div>

      <section className="section container">
        <div style={{ maxWidth: 820 }}>

          {/* ── Identity ─────────────────────────────────────────────────── */}
          <div style={{ display: "flex", gap: 32, alignItems: "flex-start", flexWrap: "wrap", marginBottom: 52 }}>
            {cms.identity?.image_url ? (
              <CmsImage s={cms.identity} title="SPAU Logo" onClick={openLightbox} />
            ) : (
              <img
                src="/Logoithad.jpg"
                alt="SPAU Logo"
                style={{ width: 200, flexShrink: 0, borderRadius: 12, border: "1px solid var(--line)" }}
              />
            )}
            <div>
              <span className="eyebrow">{isAr ? "من نحن" : "Identity"}</span>
              <h2 className="section-title">
                {cTitle("identity",
                  "Sudanese Plastic Artists Union (SPAU)",
                  "الاتحاد العام للفنانين التشكيليين السودانيين"
                )}
              </h2>
              <p style={{ color: "var(--muted)", lineHeight: 1.8, whiteSpace: "pre-line" }}>
                {cBody("identity",
                  "The Sudanese Plastic Artists Union (SPAU) is a professional body dedicated to Sudanese visual artists at home and in the diaspora. Founded to unite the voice of Sudanese artists, advocate for their interests and nurture their creative journeys at both national and international levels.",
                  "الاتحاد العام للفنانين التشكيليين السودانيين منظمة مهنية تُعنى بالفنانين التشكيليين السودانيين في الوطن والمهجر. تأسس الاتحاد لتوحيد صوت الفنانين السودانيين والدفاع عن مصالحهم ورعاية مسيرتهم الإبداعية على الصعيدين المحلي والدولي."
                )}
              </p>
              {cms.identity?.link_url && (
                <a
                  href={cms.identity.link_url}
                  target={cms.identity.link_target || "_self"}
                  rel={cms.identity.link_target === "_blank" ? "noopener noreferrer" : undefined}
                  className="btn btn-primary"
                  style={{ marginTop: 16 }}
                >
                  {isAr ? (cms.identity.link_label_ar || cms.identity.link_label_en) : (cms.identity.link_label_en || cms.identity.link_label_ar)}
                </a>
              )}
            </div>
          </div>

          {/* ── Mission / Goals ───────────────────────────────────────────── */}
          <div style={{ marginBottom: 52 }}>
            <span className="eyebrow">{isAr ? "رسالتنا" : "Our Mission"}</span>
            <h2 className="section-title">
              {cTitle("mission",
                "What we stand for",
                "أهداف الاتحاد"
              )}
            </h2>
            <ul style={{ listStyle: "none", padding: 0, margin: "20px 0 0", display: "grid", gap: 14 }}>
              {goalList.map((g, i) => (
                <li key={i} style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                  <span style={{
                    flexShrink: 0, width: 28, height: 28, borderRadius: "50%",
                    background: "var(--brand-soft)", color: "var(--brand)",
                    fontWeight: 700, fontSize: "0.85rem", display: "grid", placeItems: "center",
                  }}>
                    {i + 1}
                  </span>
                  <span style={{ color: "var(--muted)", lineHeight: 1.7, paddingTop: 4 }}>{g}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* ── Heritage ──────────────────────────────────────────────────── */}
          <div style={{ background: "var(--brand-soft)", borderRadius: "var(--radius)", padding: "32px 28px", marginBottom: 52 }}>
            <span className="eyebrow">{isAr ? "التاريخ" : "Heritage"}</span>
            <h3 style={{ fontSize: "1.4rem" }}>
              {cTitle("heritage",
                "A rich creative heritage",
                "إرث غني بالإبداع"
              )}
            </h3>
            <p style={{ color: "var(--muted)", lineHeight: 1.8, margin: 0, whiteSpace: "pre-line" }}>
              {cBody("heritage",
                "Sudanese plastic art has a legacy spanning decades of creative achievement. From the pioneers who built the globally recognised Khartoum School of art, to modern generations carrying Sudanese identity across continents — the Union is the bridge that connects that past to the future.",
                "يمتد تاريخ الفن التشكيلي السوداني لعقود من العطاء الإبداعي. من رواد الخرطوم الذين أسسوا مدرسة الخرطوم الفنية العالمية، إلى الأجيال الحديثة التي تحمل الهوية السودانية إلى أرجاء العالم — يُشكّل الاتحاد الجسر الذي يربط الماضي بالمستقبل."
              )}
            </p>
          </div>

          {/* ── Extra CMS sections ────────────────────────────────────────── */}
          {extraSections.map((s) => {
            const title = isAr ? (s.title_ar || s.title_en) : (s.title_en || s.title_ar);
            const body  = isAr ? (s.body_ar  || s.body_en)  : (s.body_en  || s.body_ar);
            const btnLabel = isAr ? (s.link_label_ar || s.link_label_en) : (s.link_label_en || s.link_label_ar);

            return (
              <div key={s.id} style={{ marginBottom: 52, direction: isAr ? "rtl" : "ltr" }}>
                {title && (
                  <>
                    <span className="eyebrow">{s.section_key.replace(/_/g, " ")}</span>
                    <h2 className="section-title">{title}</h2>
                  </>
                )}
                {s.image_url && (
                  <CmsImage s={s} title={title} onClick={openLightbox} />
                )}
                {body && (
                  <p style={{ color: "var(--muted)", lineHeight: 1.8, whiteSpace: "pre-line" }}>{body}</p>
                )}
                {s.link_url && (
                  <a
                    href={s.link_url}
                    target={s.link_target || "_self"}
                    rel={s.link_target === "_blank" ? "noopener noreferrer" : undefined}
                    className="btn btn-primary"
                    style={{ marginTop: 12 }}
                  >
                    {btnLabel || (isAr ? "اقرأ المزيد" : "Learn More")}
                  </a>
                )}
              </div>
            );
          })}

          <Link className="btn btn-primary btn-lg" to="/register">
            {t("becomeAMember")}
          </Link>
        </div>
      </section>
    </>
  );
}

export default About;
